export const TermsContent = () => (
  <>
    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">Effective October 2026</p>
    <h1 className="font-serif-display text-4xl sm:text-5xl mt-4 mb-2 leading-tight">Terms &amp; Privacy.</h1>
    <p className="text-ink-soft italic mb-10">Written plainly. Because if we can’t explain it, you shouldn’t have to agree to it.</p>
    <p className="text-sm text-ink-mute mb-10">
      Want the short, friendly version instead?{" "}
      <a href="/privacy-promise" className="text-clay hover:text-clay-dark underline underline-offset-4">Read “How we protect you”</a>.
    </p>

    <Section title="1. What Clarity Mirror is">
      A private journaling companion. You write. An AI reflects back what it notices. That’s the whole product. We are not a therapist, we do not diagnose, and nothing here is medical advice. If you are in crisis, please contact emergency services or a licensed professional.
    </Section>

    <Section title="2. Who can use Clarity Mirror (18+)">
      <p>Clarity Mirror is for adults. <b>You must be 18 years of age or older</b> to create an account or use the service. By registering, you confirm that you are at least 18. We do not knowingly collect data from anyone under 18. If we learn that an account belongs to someone younger, we will delete the account and its data. If you believe a minor is using the service, email <b>support@claritymirror.app</b>.</p>
    </Section>

    <Section title="3. Your data: what we collect and store">
      <ul>
        <li><b>Account:</b> your name, email, hashed password, referral code, subscription status.</li>
        <li><b>Entries:</b> the text you write, moods you tag, optional body signals, and the AI reflection generated for each entry.</li>
        <li><b>Payment records:</b> Stripe session ID, plan, amount, payment status. <b>We never receive, store, or see your card number.</b></li>
        <li><b>Operational logs:</b> login timestamps and IPs (kept 90 days), account deletions, data exports.</li>
      </ul>
      <p>We do not run analytics, ad trackers, or third-party pixels.</p>
    </Section>

    <Section title="4. How your data is protected">
      <ul>
        <li>All traffic is encrypted with HTTPS/TLS.</li>
        <li><b>Your journal entries are encrypted with a key unique to your account.</b> Your entries and their AI reflections are encrypted by us before they are written to the database. What is stored is unreadable text, not sentences, so your writing is protected at rest regardless of how the underlying servers are configured. Each account’s key is separate, so no single key can open anyone else’s writing.</li>
        <li>Passwords are hashed with bcrypt + a per-user salt. Nobody at Clarity Mirror can read your password.</li>
        <li>The database is hosted on Emergent’s managed infrastructure. We do not rely on their configuration for your entries to be encrypted. We encrypt them ourselves, before they leave our application.</li>
        <li>Login attempts are rate-limited to slow down credential-stuffing attacks.</li>
        <li>Sensitive account actions (login, delete, export) are logged for security investigation.</li>
      </ul>
    </Section>

    <Section title="5. Who sees your writing">
      <p>The list is short:</p>
      <ul>
        <li><b>You.</b></li>
        <li><b>Anthropic</b> (maker of Claude, the AI). Each entry is sent over encrypted API to generate the reflection. Anthropic’s terms state customer API data is <b>not</b> used to train their models. We do not send more than what is needed for the reflection.</li>
        <li><b>Emergent</b>, our hosting provider. Their engineers have platform-level access to the servers running this app, as any hosting provider does.</li>
        <li><b>Stripe</b>, our payment processor, sees your email and name at checkout. Stripe is PCI-DSS Level 1 certified.</li>
      </ul>
      <p>Nobody else. We do not sell, rent, or share your writing.</p>
    </Section>

    <Section title="6. AI and model training">
      <p>Your entries are sent to Anthropic’s Claude API to generate reflections and pattern insights. Anthropic does not train on API data by default, and neither we nor Emergent train any model on your writing. If Anthropic’s policy ever changes, we will update this document and notify you in-app before the change takes effect.</p>
    </Section>

    <Section title="7. Your rights">
      <ul>
        <li><b>Export.</b> Download everything we have about you as a readable file you can open on any device, any time, from Settings.</li>
        <li><b>Delete.</b> Permanently remove your account and every entry. Instant, irreversible.</li>
        <li><b>Access &amp; correction.</b> Edit any entry from within the app. Email us for anything else.</li>
        <li><b>Withdraw consent.</b> Delete your account. Once deleted, we retain nothing about you except anonymised deletion logs required for audit.</li>
      </ul>
    </Section>

    <Section title="8. Subscription, automatic renewal and refunds">
      <ul>
        <li>Every new account gets a 14-day free trial. No card is needed to try the app.</li>
        <li>Plans are $5/month or $45/year, billed through Stripe. <b>They renew automatically</b>, which is the point: your writing is never interrupted by an expired plan.</li>
        <li>When you start a subscription you get another 14 days free before the first charge. After that, your card is charged automatically at the start of every billing period until you cancel.</li>
        <li>If you use a discount code, the discounted price applies to the period shown at checkout. After that it renews at the standard price, unless the code says otherwise.</li>
        <li>For annual plans we email you about 7 days before each renewal charge, so nothing is ever a surprise.</li>
        <li>You can cancel any time from Settings, in one click, or by emailing us. Cancelling stops all future charges and you keep full access until the end of the period you’ve already paid for.</li>
        <li>We refund unhappy customers within 14 days of a charge, no questions asked. Email support@claritymirror.app.</li>
      </ul>
    </Section>

    <Section title="9. Not therapy, not medical advice">
      Clarity Mirror is a reflection tool. It is not therapy, not a mental health service, and not a substitute for professional care. The AI’s observations are prompts for your own thinking, not diagnoses. If you are experiencing a mental health emergency, call your local emergency number or a crisis line.
    </Section>

    <Section title="10. Acceptable use">
      Please do not use Clarity Mirror to write content that is illegal, that abuses another person, that infringes copyright, or that attempts to attack, reverse-engineer, or overload our systems. We reserve the right to terminate accounts that do.
    </Section>

    <Section title="11. Limitation of liability">
      Clarity Mirror is provided ”as is.” To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive damages, including loss of data or lost profits, arising from your use of the service. Our total liability in any 12-month period is capped at the amount you paid us during that period.
    </Section>

    <Section title="12. Breach notification">
      If we learn of a security incident affecting your personal data, we will notify you by email within 72 hours of confirming the incident and provide details of what happened, what data was affected, and steps we are taking.
    </Section>

    <Section title="13. Changes to these terms">
      If we make material changes to how we handle your data, we will notify you in-app at least 30 days before the change takes effect. Continued use after that date means you accept the new terms.
    </Section>

    <Section title="14. Intellectual property">
      <p>The Clarity Mirror software, design, layout, logic, and branding are the <b>exclusive intellectual property of Clarity Mirror</b>. Reverse-engineering, scraping, or copying the tool, in whole or in part, for commercial benefit is strictly prohibited and unlawful. Your writing remains entirely yours. The full legal terms live in our{" "}
      <a href="/terms-of-service" className="text-clay hover:text-clay-dark underline underline-offset-4"><b>Terms of Service</b></a>.</p>
    </Section>

    <Section title="15. Governing law (which laws apply)">
      Clarity Mirror is run from Virginia, in the United States, so <b>these terms are governed by the laws of the Commonwealth of Virginia</b>. If a disagreement ever couldn’t be sorted out between us, it would be settled in the Virginia courts.
    </Section>

    <Section title="16. Contact">
      Questions, requests, or concerns: <b>support@claritymirror.app</b>.
    </Section>
  </>
);

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif-display text-2xl text-ink mb-4">{title}</h2>
      <div className="text-ink-soft leading-relaxed space-y-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:space-y-2 [&_li]:relative [&_li]:pl-5 [&_li:before]:content-['·'] [&_li:before]:absolute [&_li:before]:left-0 [&_li:before]:text-clay [&_li:before]:text-xl">
        {children}
      </div>
    </section>
  );
}
